import React from 'react';
import {
	Image,
	ImageSourcePropType,
	Platform,
	StyleSheet,
	Text,
	useWindowDimensions,
	View,
} from 'react-native';
import { BaseToast, type ToastConfig } from 'react-native-toast-message';

import BigWinWhiskeyPng from '../../assets/big_win_whiskey.png';
import ChipPng from '../../assets/chip.png';
import ToastJackpot1 from '../../assets/toast_jackpot_1.png';
import ToastJackpot2 from '../../assets/toast_jackpot_2.png';
import ToastJackpot3 from '../../assets/toast_jackpot_3.png';
import ToastTournamentGoldPng from '../../assets/toast_tournament_gold.png';
import ToastVegasPng from '../../assets/toast_vegas.png';
import { badgeSource } from '../Components/Achievements/achievementsViewModel';

const jackpotImageByType: Record<number, ImageSourcePropType> = {
	1: ToastJackpot1,
	2: ToastJackpot2,
	3: ToastJackpot3,
};

function BroadcastToastScreenAlign({ children }: { children: React.ReactNode }) {
	// Toast is mounted outside the main tree on web; do not use useDevice() (needs SafeAreaProvider).
	if (Platform.OS === 'web') {
		return <View style={styles.screenRow}>{children}</View>;
	}
	return <>{children}</>;
}

function BroadcastToastLayout({
	text1,
	text2,
	imageSource,
}: {
	text1?: string;
	text2?: string;
	imageSource: ImageSourcePropType;
}) {
	const { width } = useWindowDimensions();
	const maxW = Math.min(520, width * 0.92);

	return (
		<View style={[styles.wrap, { maxWidth: maxW }]}>
			<Image source={imageSource} style={styles.icon} resizeMode='contain' />
			<View style={styles.textColumn}>
				<Text style={styles.message} numberOfLines={4}>
					{text1}
				</Text>
				{text2 ? (
					<Text style={styles.detail} numberOfLines={4}>
						{text2}
					</Text>
				) : null}
			</View>
		</View>
	);
}

export const globalBroadcastToastConfig: ToastConfig = {
	error: props => <BaseToast {...props} text1NumberOfLines={999} text2NumberOfLines={999} />,
	success: props => <BaseToast {...props} text1NumberOfLines={999} text2NumberOfLines={999} />,
	info: props => <BaseToast {...props} text1NumberOfLines={999} text2NumberOfLines={999} />,
	broadcastJackpot: ({ text1, props }) => {
		const t = Number((props as { jackpotType?: number } | undefined)?.jackpotType);
		const src = jackpotImageByType[t] || ToastJackpot1;
		return (
			<BroadcastToastScreenAlign>
				<BroadcastToastLayout text1={text1} imageSource={src} />
			</BroadcastToastScreenAlign>
		);
	},
	broadcastVegas: ({ text1 }) => (
		<BroadcastToastScreenAlign>
			<BroadcastToastLayout text1={text1} imageSource={ToastVegasPng} />
		</BroadcastToastScreenAlign>
	),
	broadcastTournament: ({ text1 }) => (
		<BroadcastToastScreenAlign>
			<BroadcastToastLayout text1={text1} imageSource={ToastTournamentGoldPng} />
		</BroadcastToastScreenAlign>
	),
	broadcastWhiskey: ({ text1 }) => (
		<BroadcastToastScreenAlign>
			<BroadcastToastLayout text1={text1} imageSource={BigWinWhiskeyPng} />
		</BroadcastToastScreenAlign>
	),
	achievementUnlocked: ({ text1, props }) => {
		const toastProps = props as { displayTier?: number; iconKey?: string } | undefined;
		const displayTier = Number(toastProps?.displayTier);
		const tier = Number.isFinite(displayTier) ? Math.round(displayTier) : 1;
		const src = badgeSource(toastProps?.iconKey, tier, false);
		return (
			<BroadcastToastScreenAlign>
				<BroadcastToastLayout text1={text1} imageSource={src} />
			</BroadcastToastScreenAlign>
		);
	},
	rewardBonus: ({ text1, text2 }) => (
		<BroadcastToastScreenAlign>
			<BroadcastToastLayout text1={text1} text2={text2} imageSource={ChipPng} />
		</BroadcastToastScreenAlign>
	),
};

const styles = StyleSheet.create({
	screenRow: {
		width: '100%',
		alignItems: 'flex-end',
		paddingRight: 12,
	},
	wrap: {
		flexDirection: 'row',
		alignItems: 'center',
		backgroundColor: 'rgba(16, 15, 20, 0.92)',
		borderRadius: 12,
		paddingVertical: 12,
		paddingHorizontal: 14,
		borderWidth: StyleSheet.hairlineWidth,
		borderColor: 'rgba(255, 215, 120, 0.35)',
	},
	icon: {
		width: 56,
		height: 56,
		flexShrink: 0,
		marginRight: 12,
	},
	textColumn: {
		flex: 1,
	},
	message: {
		color: '#f5f0e6',
		fontSize: 15,
		lineHeight: 20,
		fontWeight: '600',
	},
	detail: {
		marginTop: 2,
		color: '#f5f0e6',
		fontSize: 14,
		lineHeight: 19,
	},
});
